// HTTP client for slack-archive-service.
//
// Owns the fetch plumbing, token injection, and error normalization.
// React components import the typed methods directly; AuthContext
// owns token storage and registers the 401 handler on this module
// at boot.
//
// Why one global token here instead of threading it through every
// call site: the viewer's "auth scope" is the entire app (we're a
// single-tenant client), and components shouldn't have to know the
// token exists to render an attachment image. AuthContext calls
// setApiToken on login/logout, this module reads it on each request.

// ---------- types -----------------------------------------------------------

/**
 * Response body shape of GET /healthz. Public, no auth needed.
 */
export interface HealthResponse {
  status: string;
  version: string;
}

/**
 * One row in workspaces.json — service's index entry shape.
 *
 * Phase 5.0 cleanup: dropped ``current_master_hash`` and
 * ``snapshot_count`` from this interface — Phase 6 removed the
 * ZIP/snapshot model entirely and neither field has a meaningful
 * value in the Mongo-only world (the service returns 0 / ""
 * placeholders for them). Nothing in the viewer ever rendered
 * either field; the type now matches what the viewer actually
 * consumes. The service still serialises both as 0 / "" for
 * wire-compat with any external clients hitting the API
 * directly — that cleanup is service-side and out of this
 * phase's scope.
 */
export interface WorkspaceSummary {
  team_id: string;
  team_name: string;
  first_ingested_at: string;
  last_ingested_at: string;
}

export interface WorkspacesListResponse {
  workspaces: WorkspaceSummary[];
}

/**
 * Conversation index entry from the merged ZIP. The service preserves
 * extra Slack-export fields via extra='allow' on the pydantic model;
 * we mirror that with `[extra: string]: unknown`.
 */
export interface ApiConversation {
  id: string;
  type: "public_channel" | "private_channel" | "mpim" | "im";
  label: string;
  message_count: number;
  [extra: string]: unknown;
}

export interface ConversationsListResponse {
  conversations: ApiConversation[];
}

/**
 * Message DTO from the service. Mirrors models/message.py — Slack's
 * own fields plus our `deleted` merge-engine flag. ``replies`` is
 * always present (empty list if no thread); the API's threads
 * endpoint returns a parent with replies cleared and the replies
 * separately.
 */
export interface ApiFileRef {
  id: string;
  name?: string | null;
  mimetype?: string | null;
  filetype?: string | null;
  size?: number | null;
  _local_path?: string | null;
  _sha256?: string | null;
  [extra: string]: unknown;
}

export interface ApiReaction {
  name: string;
  users: string[];
  count: number;
}

export interface ApiEditMeta {
  user?: string | null;
  ts?: string | null;
  [extra: string]: unknown;
}

export interface ApiMessage {
  ts: string;
  user?: string | null;
  text: string;
  files: ApiFileRef[];
  reactions: ApiReaction[];
  /** Pass 6.0c.3: canonical boolean. Pre-6.0c.3 the sync writer
   *  let Slack's raw ``edited: {user,ts}`` dict pass through; the
   *  union type keeps old docs renderable until the migration
   *  flips them all to a boolean. ``isMessageMarkedEdited``
   *  handles both shapes. */
  edited?: boolean | ApiEditMeta | null;
  /** Pass 6.0c.3: ISO 8601 UTC of the latest edit Slack reported
   *  for this message. Used as a tooltip on the (edited)
   *  indicator. Null when the message has never been edited. */
  edited_at?: string | null;
  replies: ApiMessage[];
  deleted: boolean;
  /** Pass 6.0c.3: ISO 8601 UTC of when the poll first observed
   *  the message missing from Slack's response. Null until
   *  deletion is detected. */
  deleted_at?: string | null;
  /** Pass 6.0b.3 — surface the thread linkage so the renderer can
   *  detect a thread_broadcast (Slack's "Also sent as direct
   *  message" feature) and look up the parent for the preview
   *  header. Both fields are passthrough from sync_messages and
   *  remain on the ``[extra]`` index signature for callers that
   *  don't yet read them. */
  thread_ts?: string | null;
  subtype?: string | null;
  [extra: string]: unknown;
}

export interface MessagesPageResponse {
  messages: ApiMessage[];
  next_cursor: string | null;
  has_more: boolean;
  /** Pass 2.13: populated only in around mode. Null in older/newer. */
  has_more_before?: boolean | null;
  /** Pass 2.13: populated only in around mode. Null in older/newer. */
  has_more_after?: boolean | null;
}

export interface ThreadResponse {
  parent: ApiMessage;
  replies: ApiMessage[];
}

/**
 * One entry in users.json. Slack user dicts vary; preserve unknown
 * fields the same way the service does.
 */
export interface ApiUser {
  real_name?: string | null;
  display_name?: string | null;
  name?: string | null;
  deleted?: boolean;
  is_bot?: boolean;
  profile?: Record<string, unknown>;
  [extra: string]: unknown;
}

export interface UsersResponse {
  users: Record<string, ApiUser>;
}

/**
 * POST /auth/login response. Pass 2.4 returned just `{token}`;
 * Pass 2.8 also returns `expires_at` (ISO 8601 mirroring the JWT
 * exp claim) so the viewer can drive proactive re-login UX without
 * decoding the JWT body itself.
 */
export interface LoginResponse {
  token: string;
  expires_at: string;
}

// ---------- Phase 5.0: system status ----------------------------------------

/**
 * Per-workspace status row returned by /system/status. The FE
 * derives the health-dot color from these fields (see
 * lib/workspaceHealth.ts); no color/severity field is wire-
 * level — the backend stays a pure data surface.
 */
export interface SystemStatusWorkspace {
  team_id: string;
  team_name: string;
  vault_status: "active" | "needs_reauth" | "disabled" | "unknown" | string;
  /** ISO 8601 UTC; null = vault row never validated. */
  last_validated_at: string | null;
  /** ISO 8601 UTC; null = workspace never polled (fresh onboard). */
  last_polled_at: string | null;
  backfill_complete: boolean;
  /** ISO 8601 UTC; null = no messages yet (or fresh workspace). */
  last_message_at: string | null;
  /** Populated only when the most recent vault write recorded a
   *  failure diagnostic. `type` is the Slack-side machine code
   *  (invalid_auth / token_revoked / ratelimited / ...); `message`
   *  is the free-form human-readable string. */
  last_slack_error: {
    type: string | null;
    message: string | null;
    at: string | null;
  } | null;
  message_count: number;
  attachment_count: number;
  /** Phase 6.1: username of the workspace's owning user. Null only
   *  if the vault row genuinely has no ObjectId owner (shouldn't
   *  happen post-6.0b). The viewer shows it only to superadmin (a
   *  regular user sees only their own workspaces, so it's
   *  redundant for them). Optional for backward compatibility with
   *  a pre-6.1 service. */
  owned_by_username?: string | null;
}

/** Per-status counts from sync_drive_upload_queue. All four keys
 *  always present (zero when absent) so the FE never deals with
 *  optionals. `null` for the whole object when drive_enabled is
 *  false. */
export interface DriveQueueDepth {
  queued: number;
  retrying: number;
  uploading: number;
  dead: number;
}

export interface SystemStatusResponse {
  workspaces: SystemStatusWorkspace[];
  drive_enabled: boolean;
  /** Null when drive_enabled=false. */
  drive_queue: DriveQueueDepth | null;
  /** Null when drive_enabled=false. */
  cache_bytes: number | null;
  /** Null when drive_enabled=false. */
  cache_cap_bytes: number | null;
  /** Server-side timestamp the payload was assembled. The FE shows
   *  this as "Last refresh: ..." so the operator can tell if the
   *  polling has wedged. */
  as_of: string;
}

// ---------- module state ----------------------------------------------------

let _token: string | null = null;
let _onUnauthorized: (() => void) | null = null;

/**
 * Pass 6.3.2.1: requests that perform step-up auth (re-verify the
 * caller's CURRENT password — create/delete user, reset password)
 * set this header so a 401 from a wrong current-password is handled
 * inline by the modal instead of triggering the global logout. See
 * the 401 branch in ``call``.
 */
export const STEP_UP_HEADER = "X-Step-Up";
// Phase 6.2: when a superadmin selects an impersonation scope, the
// ImpersonationContext sets this module value (the SAME pattern
// AuthContext uses for the token — a module-level value set from a
// top-level provider, so non-React call sites stay context-free).
// Every authenticated request then carries X-Impersonate-User. Null
// = no impersonation = no header = existing all-users behaviour.
let _impersonateUserId: string | null = null;

/**
 * Set the bearer token used on every subsequent request. Pass null
 * to clear (e.g. on logout).
 */
export function setApiToken(token: string | null): void {
  _token = token;
}

/**
 * Phase 6.2: set (or clear, with null) the impersonation target.
 * When non-null, every subsequent authenticated request includes
 * the ``X-Impersonate-User`` header. The backend validates it
 * (superadmin-only; valid target) and scopes the request to that
 * user. Set synchronously by ImpersonationContext.setImpersonation
 * so the very next fetch carries the header.
 */
export function setImpersonationUserId(userId: string | null): void {
  _impersonateUserId = userId;
}

/** Read-back for tests. */
export function getImpersonationUserId(): string | null {
  return _impersonateUserId;
}

/**
 * Read-back for tests + the ingest progress UI which needs the token
 * to construct an XHR upload. Don't expose the token elsewhere.
 */
export function getApiToken(): string | null {
  return _token;
}

/**
 * Register a callback that fires whenever the service returns 401.
 * AuthContext sets this to its logout function so a stale token
 * surface routes the user back to the login screen automatically.
 *
 * Pass null to clear (e.g. when AuthContext unmounts in tests).
 */
export function setUnauthorizedHandler(cb: (() => void) | null): void {
  _onUnauthorized = cb;
}

/**
 * Service base URL.
 *
 * Two modes:
 *   - VITE_SERVICE_URL set at build time (e.g. the API's own domain,
 *     https://api.slack-chat-backup.webmobtech.biz) → the bundle calls
 *     that absolute origin directly (cross-origin; the service sends
 *     CORS *). A trailing slash is trimmed so paths like "/setup/status"
 *     concatenate cleanly.
 *   - VITE_SERVICE_URL unset → same-origin "/api" (Pass 2.12.2), which
 *     the viewer's nginx proxies to the service. Keeps the proxy
 *     deployment working with no rebuild.
 */
export function getApiBaseUrl(): string {
  const configured = import.meta.env.VITE_SERVICE_URL;
  if (configured && configured.trim() !== "") {
    return configured.replace(/\/+$/, "");
  }
  return "/api";
}

// ---------- error type ------------------------------------------------------

/**
 * Single error type for everything that can go wrong in an API call.
 * status:
 *   0   — network error (fetch threw, no HTTP response received)
 *   400+ — service returned a structured error
 *
 * Components do `if (e instanceof ApiError && e.status === 404) ...`
 * for typed handling.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly responseBody?: unknown;

  constructor(message: string, status: number, responseBody?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.responseBody = responseBody;
  }
}

// ---------- low-level call --------------------------------------------------

interface CallOpts {
  method?: string;
  body?: BodyInit;
  headers?: Record<string, string>;
  // signal — for cancellation if a component unmounts mid-fetch
  signal?: AbortSignal;
  // expectJson defaults to true; pass false for endpoints that return
  // raw bytes (attachment download, merged ZIP download).
  expectJson?: boolean;
}

async function call(path: string, opts: CallOpts = {}): Promise<Response> {
  const url = `${getApiBaseUrl()}${path}`;
  const headers = new Headers(opts.headers);
  if (_token) {
    headers.set("Authorization", `Bearer ${_token}`);
  }
  // Phase 6.2: attach the impersonation header when a scope is
  // active. Backwards-compatible — absent when not impersonating.
  if (_impersonateUserId) {
    headers.set("X-Impersonate-User", _impersonateUserId);
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? "GET",
      body: opts.body,
      headers,
      signal: opts.signal,
    });
  } catch (e) {
    // Network-level failure — DNS, connection refused, CORS, abort.
    // status=0 is the convention for "no HTTP response at all".
    const msg = e instanceof Error ? e.message : String(e);
    throw new ApiError(`network error: ${msg}`, 0);
  }

  if (res.status === 401) {
    // Pass 6.3.2.1 (BUG 1): step-up auth failures (wrong CURRENT
    // password) also come back as 401, but they are NOT a session
    // expiry — they must surface inline in the step-up modal, not
    // log the operator out. Step-up requests tag themselves with
    // ``X-Step-Up: true``; for those we skip the global logout
    // handler and just throw so the caller can render the inline
    // error. Every other 401 is treated as session expiry.
    const isStepUp = headers.get(STEP_UP_HEADER) === "true";
    if (!isStepUp && _onUnauthorized) _onUnauthorized();
    throw new ApiError("unauthorized", 401);
  }

  return res;
}

async function callJson<T>(path: string, opts: CallOpts = {}): Promise<T> {
  const res = await call(path, opts);
  if (!res.ok) {
    let detail = `HTTP ${res.status} ${res.statusText}`;
    let body: unknown = undefined;
    try {
      body = await res.json();
      const fromBody = (body as { detail?: unknown })?.detail;
      if (typeof fromBody === "string") {
        detail = `${res.status}: ${fromBody}`;
      }
    } catch {
      // Response wasn't JSON. Keep the status-line detail.
    }
    throw new ApiError(detail, res.status, body);
  }
  try {
    return (await res.json()) as T;
  } catch (e) {
    throw new ApiError(
      `response not valid JSON: ${(e as Error).message}`,
      res.status,
    );
  }
}

async function callBlob(path: string, opts: CallOpts = {}): Promise<Blob> {
  const res = await call(path, opts);
  if (!res.ok) {
    let detail = `HTTP ${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      const fromBody = (body as { detail?: unknown })?.detail;
      if (typeof fromBody === "string") {
        detail = `${res.status}: ${fromBody}`;
      }
    } catch {
      // not JSON; ignore
    }
    throw new ApiError(detail, res.status);
  }
  return await res.blob();
}

// ---------- public methods --------------------------------------------------

export async function ping(signal?: AbortSignal): Promise<HealthResponse> {
  return callJson<HealthResponse>("/healthz", { signal });
}

/**
 * POST /auth/login — exchange username + password for a bearer
 * token. Pass 2.4. The endpoint is public (no Authorization
 * header needed); the caller stores the returned token via
 * AuthContext.login.
 */
export async function login(
  username: string,
  password: string,
  signal?: AbortSignal,
): Promise<LoginResponse> {
  return callJson<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
    headers: { "Content-Type": "application/json" },
    signal,
  });
}

export async function listWorkspaces(
  signal?: AbortSignal,
): Promise<WorkspacesListResponse> {
  return callJson<WorkspacesListResponse>("/workspaces", { signal });
}

export async function getWorkspace(
  teamID: string,
  signal?: AbortSignal,
): Promise<WorkspaceSummary> {
  return callJson<WorkspaceSummary>(
    `/workspaces/${encodeURIComponent(teamID)}`,
    { signal },
  );
}

/**
 * Pass 2.15: remove a workspace from the index. Returns void on
 * the service's 204 No Content. Throws ApiError on any non-2xx
 * (404 = workspace already gone, 401 = auth lapse, etc.).
 *
 * Pass 3.8 cutover: this only deletes the workspace index entry
 * — the sync subsystem still owns the underlying messages /
 * attachments / vault / state collections (and the on-disk
 * binary pool), and operating on those is its job. The previous
 * ``deleteDrive`` query parameter is gone with the Drive feature.
 */
export async function deleteWorkspace(
  teamID: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await call(`/workspaces/${encodeURIComponent(teamID)}`, {
    method: "DELETE",
    signal,
  });
  if (res.status === 204) return;
  if (!res.ok) {
    let detail = `HTTP ${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      const fromBody = (body as { detail?: unknown })?.detail;
      if (typeof fromBody === "string") detail = `${res.status}: ${fromBody}`;
    } catch {
      // not JSON; keep the status-line detail
    }
    throw new ApiError(detail, res.status);
  }
  // Any other 2xx (shouldn't happen — service returns 204 or
  // throws): treat as success.
}

export async function listConversations(
  teamID: string,
  signal?: AbortSignal,
): Promise<ConversationsListResponse> {
  return callJson<ConversationsListResponse>(
    `/workspaces/${encodeURIComponent(teamID)}/conversations`,
    { signal },
  );
}

export interface ListMessagesOpts {
  cursor?: string;
  limit?: number;
  direction?: "newer" | "older" | "around";
  /** Pass 2.13: required when direction='around'. */
  targetTs?: string;
  signal?: AbortSignal;
}

export async function listMessages(
  teamID: string,
  convID: string,
  opts: ListMessagesOpts = {},
): Promise<MessagesPageResponse> {
  const qs = new URLSearchParams();
  if (opts.cursor) qs.set("cursor", opts.cursor);
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  if (opts.direction) qs.set("direction", opts.direction);
  if (opts.targetTs) qs.set("target_ts", opts.targetTs);
  const query = qs.toString();
  const path =
    `/workspaces/${encodeURIComponent(teamID)}` +
    `/conversations/${encodeURIComponent(convID)}/messages` +
    (query ? `?${query}` : "");
  return callJson<MessagesPageResponse>(path, { signal: opts.signal });
}

export async function getThread(
  teamID: string,
  convID: string,
  parentTs: string,
  signal?: AbortSignal,
): Promise<ThreadResponse> {
  return callJson<ThreadResponse>(
    `/workspaces/${encodeURIComponent(teamID)}` +
      `/conversations/${encodeURIComponent(convID)}` +
      `/threads/${encodeURIComponent(parentTs)}`,
    { signal },
  );
}

export async function listUsers(
  teamID: string,
  signal?: AbortSignal,
): Promise<UsersResponse> {
  return callJson<UsersResponse>(
    `/workspaces/${encodeURIComponent(teamID)}/users`,
    { signal },
  );
}

/**
 * Fetch an attachment as a Blob. Components wrap this in
 * URL.createObjectURL to get a src=URL the browser can render
 * (the auth header is required, so we can't just put the API URL
 * directly in a <img src>).
 */
export async function getAttachmentBlob(
  teamID: string,
  fileID: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return callBlob(
    `/workspaces/${encodeURIComponent(teamID)}` +
      `/files/${encodeURIComponent(fileID)}`,
    { signal },
  );
}

interface StreamUrlResponse {
  stream_url: string;
  mimetype: string | null;
  size: number | null;
}

/**
 * For <video>/<audio>: get a same-origin, range-capable stream URL the
 * media element can use DIRECTLY (it can't send the Bearer header, so we
 * exchange the authed session for a short-lived, file-scoped token here).
 * Avoids blob-downloading a multi-hundred-MB file just to play it, and
 * the service streams from disk or proxies Drive — no Drive URL is ever
 * exposed to the browser. Returns the absolute URL (base + token'd path).
 */
export async function fetchStreamUrl(
  teamID: string,
  fileID: string,
  signal?: AbortSignal,
): Promise<{ url: string; mimetype: string | null; size: number | null }> {
  const data = await callJson<StreamUrlResponse>(
    `/workspaces/${encodeURIComponent(teamID)}` +
      `/files/${encodeURIComponent(fileID)}/stream-url`,
    { signal },
  );
  return {
    url: `${getApiBaseUrl()}${data.stream_url}`,
    mimetype: data.mimetype,
    size: data.size,
  };
}

// Pass 3.8 cutover: ``listSnapshots``, ``getMergeLog``,
// ``downloadMerged``, ``getDriveStatus``, and the
// ``retryDrive*`` helpers used to live here. They're gone with
// the service-side endpoints they wrapped — the chain-of-custody
// + Drive-backup features were ZIP-pipeline artefacts that the
// sync-driven model doesn't have an analogue for.

// ---------- search (Pass 2.13) ---------------------------------------------

export type SearchScope = "conversation" | "workspace" | "all";

export interface SearchHit {
  team_id: string;
  team_name: string;
  conversation_id: string;
  conversation_label: string;
  conversation_type: string;
  ts: string;
  parent_ts: string | null;
  user_id: string | null;
  user_display_name: string;
  text: string;
  deleted: boolean;
  has_files: boolean;
  files: ApiFileRef[];
}

export interface SearchResponse {
  hits: SearchHit[];
  has_more: boolean;
  next_cursor: string | null;
  total_count: number;
}

export interface SearchOpts {
  q: string;
  /** Workspace-scoped search: 'conversation' (requires convID) or
   *  'workspace'. Cross-workspace search uses 'all' and the
   *  searchAll() function below. */
  scope: SearchScope;
  teamID?: string;
  convID?: string;
  fromUserId?: string;
  before?: string;
  after?: string;
  hasFiles?: boolean | null;
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
}

function _searchQS(opts: SearchOpts): string {
  const qs = new URLSearchParams();
  qs.set("q", opts.q);
  qs.set("scope", opts.scope);
  if (opts.convID) qs.set("conv_id", opts.convID);
  if (opts.fromUserId) qs.set("from_user_id", opts.fromUserId);
  if (opts.before) qs.set("before", opts.before);
  if (opts.after) qs.set("after", opts.after);
  if (opts.hasFiles !== undefined && opts.hasFiles !== null) {
    qs.set("has_files", String(opts.hasFiles));
  }
  if (opts.cursor) qs.set("cursor", opts.cursor);
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  return qs.toString();
}

export async function searchWorkspace(
  opts: SearchOpts,
): Promise<SearchResponse> {
  if (!opts.teamID) {
    throw new Error("searchWorkspace requires teamID");
  }
  return callJson<SearchResponse>(
    `/workspaces/${encodeURIComponent(opts.teamID)}/search?${_searchQS(opts)}`,
    { signal: opts.signal },
  );
}

export async function searchAll(opts: SearchOpts): Promise<SearchResponse> {
  return callJson<SearchResponse>(`/search?${_searchQS(opts)}`, {
    signal: opts.signal,
  });
}

/**
 * Phase 5.0: GET /system/status — aggregate health across every
 * workspace + Drive queue depth + cache usage. Polled every 60s
 * by SystemStatusProvider; consumed by the workspace-rail health
 * badge + the /status diagnostic page.
 */
export async function getSystemStatus(
  signal?: AbortSignal,
): Promise<SystemStatusResponse> {
  return callJson<SystemStatusResponse>("/system/status", { signal });
}

/* ---------- Phase 7.3: Drive quota (superadmin /status card) ----------
 *
 * GET /admin/drive-quota — the OAuth account's Drive storage picture:
 *   total_bytes         — the account's storage cap (null = unlimited).
 *   account_usage_bytes — TOTAL Drive usage across the whole account,
 *                         not just the Slack archive.
 *   archive_used_bytes  — recursive byte sum of the Slack archive tree
 *                         (the archive's slice of the account).
 *   free_bytes          — total - account_usage (null when unlimited);
 *                         the honest free figure (subtracts ALL usage).
 *   computed_at         — ISO-8601 UTC timestamp of the computation.
 *
 * Superadmin-only; 503 (drive_disabled / archive_root_not_found /
 * drive_unavailable) on the graceful-error paths.
 */
export interface DriveQuota {
  total_bytes: number | null;
  account_usage_bytes: number;
  archive_used_bytes: number;
  free_bytes: number | null;
  computed_at: string;
}

export async function getDriveQuota(signal?: AbortSignal): Promise<DriveQuota> {
  return callJson<DriveQuota>("/admin/drive-quota", { signal });
}

/* ---------- Phase 6.0a.1: first-run setup wizard ----------
 *
 * Two endpoints, both unauthenticated:
 *   GET  /setup/status  → {setup_required}
 *   POST /setup         → {token, expires_at} (201)
 *
 * The viewer fetches /setup/status once on app boot via
 * SetupStatusContext; if setup_required is true every route is
 * redirected to /setup until POST /setup succeeds and the new
 * superadmin's token is stored.
 */

export interface SetupStatusResponse {
  setup_required: boolean;
}

export async function getSetupStatus(
  signal?: AbortSignal,
): Promise<SetupStatusResponse> {
  return callJson<SetupStatusResponse>("/setup/status", { signal });
}

/** Field-shaped errors the /setup endpoint returns on a 422.
 *  Each key is a request body field; each value is a single
 *  human-readable string the FE renders inline. */
export interface SetupValidationErrors {
  username?: string;
  email?: string;
  password?: string;
  password_confirm?: string;
}

export interface SetupSuccessResponse {
  token: string;
  expires_at: string;
}

export async function postSetup(
  body: {
    username: string;
    email: string;
    password: string;
    password_confirm: string;
  },
  signal?: AbortSignal,
): Promise<SetupSuccessResponse> {
  return callJson<SetupSuccessResponse>("/setup", {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
    signal,
  });
}

/* ---------- Phase 6.1: user management (superadmin) ----------
 *
 * Mirrors service/src/.../api/admin_users.py. All destructive ops
 * require the caller's CURRENT password (step-up auth) in the body.
 * The server re-checks role on every call; these helpers just shape
 * the requests + responses.
 */

export interface ManagedUser {
  user_id: string;
  username: string;
  email: string | null;
  role: "superadmin" | "user" | string;
  created_at: string;
  created_by_username: string | null;
}

export interface UsersListResponse {
  users: ManagedUser[];
}

export async function listManagedUsers(
  signal?: AbortSignal,
): Promise<UsersListResponse> {
  return callJson<UsersListResponse>("/users", { signal });
}

export interface CreateUserResponse {
  user_id: string;
  username: string;
  email: string | null;
  role: string;
  created_at: string;
  created_by_username: string | null;
}

export async function createUser(
  body: {
    username: string;
    email: string;
    password: string;
    role: "user" | "superadmin";
    current_password: string;
  },
  signal?: AbortSignal,
): Promise<CreateUserResponse> {
  return callJson<CreateUserResponse>("/users", {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json", [STEP_UP_HEADER]: "true" },
    signal,
  });
}

/** Phase 8.2 — per-user workspace management (superadmin). */
export interface UserWorkspaceEntry {
  team_id: string;
  team_name: string | null;
  backfill_complete: boolean;
  message_count: number;
  attachment_count: number;
}

export interface UserWorkspacesResponse {
  user_id: string;
  username: string;
  workspaces: UserWorkspaceEntry[];
}

export async function listUserWorkspaces(
  userId: string,
  signal?: AbortSignal,
): Promise<UserWorkspacesResponse> {
  return callJson<UserWorkspacesResponse>(
    `/users/${encodeURIComponent(userId)}/workspaces`,
    { signal },
  );
}

/** DELETE /users/<id>/workspaces/<team> — hard-delete ONE owner's
 *  archive of a workspace (superadmin). Irreversible. */
export async function deleteUserWorkspace(
  userId: string,
  teamID: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await call(
    `/users/${encodeURIComponent(userId)}/workspaces/${encodeURIComponent(teamID)}`,
    { method: "DELETE", signal },
  );
  if (res.status === 204) return;
  if (!res.ok) {
    let detail = `HTTP ${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      const fromBody = (body as { detail?: unknown })?.detail;
      if (typeof fromBody === "string") detail = `${res.status}: ${fromBody}`;
    } catch {
      // not JSON; keep the status-line detail
    }
    throw new ApiError(detail, res.status);
  }
}

/** DELETE /users/<id> — cascade-delete a user + all their data.
 *  Step-up: caller's current password in the body. */
export async function deleteUser(
  userId: string,
  currentPassword: string,
  signal?: AbortSignal,
): Promise<unknown> {
  return callJson<unknown>(`/users/${encodeURIComponent(userId)}`, {
    method: "DELETE",
    body: JSON.stringify({ current_password: currentPassword }),
    headers: { "Content-Type": "application/json", [STEP_UP_HEADER]: "true" },
    signal,
  });
}

/** PATCH /users/<id>/password — superadmin resets another user's
 *  password. Step-up: caller's current password. */
export interface McpTokenResponse {
  token: string;
  expires_at: string;
  purpose: string;
}

/** Stage 2b — mint a long-lived READ-ONLY MCP connector token for the
 *  signed-in user. Step-up authed (current password in body); tagged
 *  X-Step-Up so a wrong password doesn't trigger the global logout. */
export async function createMcpToken(
  currentPassword: string,
  signal?: AbortSignal,
): Promise<McpTokenResponse> {
  return callJson<McpTokenResponse>("/auth/mcp-token", {
    method: "POST",
    body: JSON.stringify({ current_password: currentPassword }),
    headers: { "Content-Type": "application/json", [STEP_UP_HEADER]: "true" },
    signal,
  });
}

export async function resetUserPassword(
  userId: string,
  currentPassword: string,
  newPassword: string,
  signal?: AbortSignal,
): Promise<unknown> {
  return callJson<unknown>(
    `/users/${encodeURIComponent(userId)}/password`,
    {
      method: "PATCH",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
      headers: { "Content-Type": "application/json", [STEP_UP_HEADER]: "true" },
      signal,
    },
  );
}

/** PATCH /users/me/password — change own password. Step-up:
 *  caller's current password. Used for the self-row. */
export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
  signal?: AbortSignal,
): Promise<unknown> {
  return callJson<unknown>("/users/me/password", {
    method: "PATCH",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
    headers: { "Content-Type": "application/json", [STEP_UP_HEADER]: "true" },
    signal,
  });
}

/* ---------- Phase 6.0b: pending-workspace attribution (superadmin) -------
 *
 * Mirrors service/src/.../api/pending_workspaces.py. When the sync
 * login fan-out discovers a Slack workspace whose auth.test email
 * doesn't match any sync_users.email, the vault row is parked with
 * status="pending_attribution" and user_id=null — the controller
 * never polls it until a superadmin attributes (binds to an existing
 * user) or rejects it. Both writes are step-up authed (the caller's
 * current password); the STEP_UP_HEADER tells call() to surface a 401
 * inline in the modal rather than logging the operator out.
 */

export interface PendingWorkspace {
  vault_id: string;
  team_id: string;
  team_name: string | null;
  /** Slack-side email that matched no sync_users row. */
  pending_email: string | null;
  status: string;
  /** ISO-8601 (UTC) of when the login fan-out discovered it; null if absent. */
  discovered_at: string | null;
}

export interface PendingWorkspacesResponse {
  pending: PendingWorkspace[];
}

export async function listPendingWorkspaces(
  signal?: AbortSignal,
): Promise<PendingWorkspacesResponse> {
  return callJson<PendingWorkspacesResponse>("/pending-workspaces", { signal });
}

/** POST /pending-workspaces/<vaultId>/create-and-attribute — the
 *  sole attribution path (Phase 6.0b revised): create the account for
 *  the workspace's Slack-side email and bind the workspace to it,
 *  flipping it active so the sync controller starts polling. If an
 *  account already owns that email, it's attributed to that account
 *  instead (the username/password/role are then ignored). Step-up
 *  authed. Returns the created (or matched) account, with `created`
 *  indicating which happened. */
export async function createAndAttributePendingWorkspace(
  vaultId: string,
  body: {
    username: string;
    email: string;
    password: string;
    role: "user" | "superadmin";
    current_password: string;
  },
  signal?: AbortSignal,
): Promise<CreateUserResponse & { created: boolean; team_id: string | null }> {
  return callJson(
    `/pending-workspaces/${encodeURIComponent(vaultId)}/create-and-attribute`,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json", [STEP_UP_HEADER]: "true" },
      signal,
    },
  );
}

/** POST /pending-workspaces/<vaultId>/reject — discard a pending
 *  workspace entirely (deletes the vault row + its orphan session
 *  dir). Step-up authed. */
export async function rejectPendingWorkspace(
  vaultId: string,
  currentPassword: string,
  signal?: AbortSignal,
): Promise<unknown> {
  return callJson<unknown>(
    `/pending-workspaces/${encodeURIComponent(vaultId)}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword }),
      headers: { "Content-Type": "application/json", [STEP_UP_HEADER]: "true" },
      signal,
    },
  );
}

/* ---------- Phase 8.0 (Option C): web-driven "Add workspace" ----------
 *
 * Superadmin starts a login; the service relays to the sync logind
 * daemon, which runs the headful Slack login on a virtual display and
 * streams it back via noVNC. The flow:
 *   1. startWorkspaceLogin(password) → {id, token, novnc paths}
 *   2. window.open(buildNoVncUrl(...)) → operator signs into Slack
 *   3. poll getLoginSessionStatus(id) / finalizeLoginSession(id)
 *      → per-workspace results (active | pending_attribution | failed)
 */

export interface StartLoginResponse {
  id: string;
  state: string;
  token: string;
  novnc_static_path: string;
  ws_path: string;
}

export interface LoginTeamResult {
  team_id: string | null;
  team_name: string | null;
  status: string;
  detail: string | null;
  attributed_email: string | null;
  replaced: boolean;
}

export interface LoginSessionStatus {
  id: string;
  state: string;
  error?: string | null;
  results?: LoginTeamResult[];
}

/** POST /admin/login-sessions — step-up (current password in body). */
export async function startWorkspaceLogin(
  currentPassword: string,
  signal?: AbortSignal,
): Promise<StartLoginResponse> {
  return callJson<StartLoginResponse>("/admin/login-sessions", {
    method: "POST",
    body: JSON.stringify({ current_password: currentPassword }),
    headers: { "Content-Type": "application/json", [STEP_UP_HEADER]: "true" },
    signal,
  });
}

export async function getLoginSessionStatus(
  id: string,
  signal?: AbortSignal,
): Promise<LoginSessionStatus> {
  return callJson<LoginSessionStatus>(
    `/admin/login-sessions/${encodeURIComponent(id)}`,
    { signal },
  );
}

export async function finalizeLoginSession(
  id: string,
  signal?: AbortSignal,
): Promise<LoginSessionStatus> {
  return callJson<LoginSessionStatus>(
    `/admin/login-sessions/${encodeURIComponent(id)}/finalize`,
    { method: "POST", signal },
  );
}

export async function cancelLoginSession(
  id: string,
  signal?: AbortSignal,
): Promise<unknown> {
  return callJson<unknown>(
    `/admin/login-sessions/${encodeURIComponent(id)}/cancel`,
    { method: "POST", signal },
  );
}

/**
 * Build the noVNC tab URL from a start response. The page loads from
 * the service's static proxy (via /api). noVNC resolves its `path`
 * param RELATIVE TO THE noVNC PAGE URL (`.../novnc/vnc.html`) — not the
 * origin root — so the WS path is `../ws` (up out of `novnc/`). That
 * resolves to `.../login-sessions/<id>/ws` regardless of the /api
 * prefix, and the one-time token rides along so the WebSocket reaches
 * the service's token-gated stream.
 */
export function buildNoVncUrl(start: StartLoginResponse): string {
  const base = getApiBaseUrl(); // "/api"
  const params = new URLSearchParams({
    autoconnect: "true",
    resize: "remote",
    reconnect: "true",
    path: `../ws?token=${encodeURIComponent(start.token)}`,
  });
  return `${base}${start.novnc_static_path}?${params.toString()}`;
}
